import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Brain, TrendingUp, Package, AlertTriangle, CheckCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
};

const PriorityBadge = ({ priority }) => {
  const colors = {
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
  };

  const labels = {
    critical: 'Crítico',
    high: 'Alto',
    medium: 'Medio',
    low: 'Bajo'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${colors[priority]}`}>
      {labels[priority]}
    </span>
  );
};

export default function AIPredictionsPage() {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchPredictions();
  }, []);

  const fetchPredictions = async () => {
    try {
      const response = await axios.get(`${API}/ai/purchase-predictions`);
      setPredictions(response.data);
    } catch (error) {
      toast.error('Error al cargar predicciones');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPredictions = predictions.filter(p => {
    if (filter === 'all') return true;
    return p.priority === filter;
  });

  const totalRecommendedCost = predictions.reduce(
    (sum, p) => sum + p.estimated_purchase_cost,
    0
  );

  const criticalCount = predictions.filter(p => p.priority === 'critical').length;
  const highCount = predictions.filter(p => p.priority === 'high').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Analizando datos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-predictions-page">
      <div className="flex items-center space-x-3">
        <div className="p-3 bg-indigo-600 rounded-2xl">
          <Brain className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Predicciones IA</h1>
          <p className="text-muted-foreground mt-1">
            Recomendaciones inteligentes de compra basadas en análisis de ventas
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Productos Críticos
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{criticalCount}</div>
            <p className="text-xs text-muted-foreground mt-1">Requieren atención inmediata</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Prioridad Alta
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{highCount}</div>
            <p className="text-xs text-muted-foreground mt-1">Comprar pronto</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Productos
            </CardTitle>
            <Package className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{predictions.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Analizados</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Inversión Recomendada
            </CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {formatCLP(totalRecommendedCost)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Para próximos 30 días</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          size="sm"
          data-testid="filter-all"
        >
          Todos ({predictions.length})
        </Button>
        <Button
          variant={filter === 'critical' ? 'default' : 'outline'}
          onClick={() => setFilter('critical')}
          size="sm"
          data-testid="filter-critical"
        >
          Críticos ({criticalCount})
        </Button>
        <Button
          variant={filter === 'high' ? 'default' : 'outline'}
          onClick={() => setFilter('high')}
          size="sm"
          data-testid="filter-high"
        >
          Altos ({highCount})
        </Button>
        <Button
          variant={filter === 'medium' ? 'default' : 'outline'}
          onClick={() => setFilter('medium')}
          size="sm"
        >
          Medios
        </Button>
        <Button
          variant={filter === 'low' ? 'default' : 'outline'}
          onClick={() => setFilter('low')}
          size="sm"
        >
          Bajos
        </Button>
      </div>

      {/* Predictions List */}
      <div className="space-y-4">
        {filteredPredictions.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay predicciones para este filtro</p>
            </CardContent>
          </Card>
        ) : (
          filteredPredictions.map((prediction) => (
            <Card
              key={prediction.product_id}
              className="hover:shadow-lg transition-shadow"
              data-testid={`prediction-${prediction.product_id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-lg">{prediction.product_name}</h3>
                      <PriorityBadge priority={prediction.priority} />
                    </div>
                    <p className="text-sm text-muted-foreground font-mono mb-4">
                      SKU: {prediction.sku} • {prediction.category}
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Stock Actual</p>
                        <p className="text-lg font-bold font-mono">{prediction.current_stock}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Ventas (30d)</p>
                        <p className="text-lg font-bold font-mono">
                          {prediction.total_sold_30_days}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Promedio Diario</p>
                        <p className="text-lg font-bold font-mono">
                          {prediction.avg_daily_sales}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Días Restantes</p>
                        <p
                          className={`text-lg font-bold font-mono ${
                            prediction.days_until_stockout < 7
                              ? 'text-red-600'
                              : prediction.days_until_stockout < 14
                              ? 'text-orange-600'
                              : 'text-green-600'
                          }`}
                        >
                          {prediction.days_until_stockout > 100
                            ? '∞'
                            : Math.round(prediction.days_until_stockout)}
                        </p>
                      </div>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground mb-2">
                        <span className="font-semibold">Análisis:</span> {prediction.reason}
                      </p>
                      <p className="text-sm">
                        <span className="font-semibold">Proyección:</span> Se estima vender{' '}
                        <span className="font-mono font-bold">
                          {Math.round(prediction.predicted_sales_30_days)}
                        </span>{' '}
                        unidades en los próximos 30 días.
                      </p>
                    </div>
                  </div>

                  <div className="ml-6 text-right">
                    <p className="text-sm text-muted-foreground mb-2">Comprar</p>
                    <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-lg p-4 mb-3">
                      <p className="text-3xl font-bold font-mono text-indigo-600 dark:text-indigo-400">
                        {Math.round(prediction.recommended_purchase_quantity)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">unidades</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Inversión estimada</p>
                      <p className="text-lg font-mono font-bold">
                        {formatCLP(prediction.estimated_purchase_cost)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}